import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Card, Row, Col } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { groupsAPI } from '../services/api';

const GroupForm = ({ isEdit = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    organizationName: '',
    contactInfo: '',
    isActive: true,
    // Campaign fields
    campaignName: 'Initial Campaign',
    campaignDescription: '',
    campaignStartDate: new Date().toISOString().split('T')[0],
    campaignEndDate: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0],
    campaignIsActive: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const navigate = useNavigate();
  const { id } = useParams();
  
  useEffect(() => {
    // If editing, fetch group data
    if (isEdit && id) {
      const fetchGroup = async () => {
        try {
          setLoading(true);
          const response = await groupsAPI.getGroupById(id);
          const group = response.data.data;
          
          setFormData({
            name: group.name || '',
            code: group.code || '',
            organizationName: group.organizationName || '',
            contactInfo: group.contactInfo || '',
            isActive: group.isActive !== undefined ? group.isActive : true
          });
          
          setLoading(false);
        } catch (err) {
          console.error('Error fetching group:', err);
          setError('Failed to load group data');
          setLoading(false);
        }
      };
      
      fetchGroup();
    }
  }, [isEdit, id]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    
    try {
      // Validate form
      if (!formData.name || !formData.code || !formData.organizationName) {
        setError('Name, code, and organization name are required');
        setLoading(false);
        return;
      }
      
      // Validate campaign fields if creating a new group
      if (!isEdit) {
        if (!formData.campaignName) {
          setError('Campaign name is required');
          setLoading(false);
          return;
        }
        
        if (!formData.campaignStartDate || !formData.campaignEndDate) {
          setError('Campaign start date and end date are required');
          setLoading(false);
          return;
        }
        
        const startDate = new Date(formData.campaignStartDate);
        const endDate = new Date(formData.campaignEndDate);
        
        if (endDate < startDate) {
          setError('Campaign end date cannot be before start date');
          setLoading(false);
          return;
        }
      }
      
      let response;
      
      if (isEdit && id) {
        // Update existing group
        response = await groupsAPI.updateGroup(id, {
          name: formData.name,
          organizationName: formData.organizationName,
          contactInfo: formData.contactInfo,
          isActive: formData.isActive
        });
        setSuccess('Group updated successfully');
      } else {
        // Create new group
        const groupData = {
          name: formData.name,
          code: formData.code,
          organizationName: formData.organizationName,
          contactInfo: formData.contactInfo,
          isActive: formData.isActive
        };
        
        response = await groupsAPI.createGroup(groupData);
        const newGroupId = response.data.data._id;
        
        // Create initial campaign for the group
        const campaignData = {
          name: formData.campaignName,
          startDate: formData.campaignStartDate,
          endDate: formData.campaignEndDate,
          description: formData.campaignDescription,
          isActive: formData.campaignIsActive
        };
        
        await groupsAPI.createCampaign(newGroupId, campaignData);
        
        setSuccess('Group and initial campaign created successfully');
        
        // Reset form after successful creation
        setFormData({
          name: '',
          code: '',
          organizationName: '',
          contactInfo: '',
          isActive: true,
          campaignName: 'Initial Campaign',
          campaignDescription: '',
          campaignStartDate: new Date().toISOString().split('T')[0],
          campaignEndDate: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0],
          campaignIsActive: true
        });
      }
      
      // Navigate after a short delay to show success message
      setTimeout(() => {
        navigate('/admin/groups');
      }, 1500);
      
    } catch (err) {
      console.error('Group form error:', err);
      setError(
        err.response?.data?.message || 
        'An error occurred. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Card className="mb-4">
      <Card.Header>
        <h4>{isEdit ? 'Edit Group' : 'Add New Group'}</h4>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Group Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter group name"
              required
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Group Code</Form.Label>
            <Form.Control
              type="text"
              name="code"
              value={formData.code}
              onChange={handleChange}
              placeholder="Enter unique group code"
              required
              disabled={isEdit} // Can't change code if editing
            />
            <Form.Text className="text-muted">
              This code will be used by participants to log in
            </Form.Text>
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Organization Name</Form.Label>
            <Form.Control
              type="text"
              name="organizationName"
              value={formData.organizationName}
              onChange={handleChange}
              placeholder="Enter organization name"
              required
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Contact Information</Form.Label>
            <Form.Control
              type="text"
              name="contactInfo"
              value={formData.contactInfo}
              onChange={handleChange}
              placeholder="Enter contact information"
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              name="isActive"
              label="Active"
              checked={formData.isActive}
              onChange={handleChange}
            />
          </Form.Group>
          
          {!isEdit && (
            <>
              <hr className="my-4" />
              <h5>Initial Campaign</h5>
              <p className="text-muted">Set the date range when users can access the system</p>
              
              <Form.Group className="mb-3">
                <Form.Label>Campaign Name</Form.Label>
                <Form.Control
                  type="text"
                  name="campaignName"
                  value={formData.campaignName}
                  onChange={handleChange}
                  placeholder="Enter campaign name"
                  required
                />
              </Form.Group>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="campaignStartDate"
                      value={formData.campaignStartDate}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="campaignEndDate"
                      value={formData.campaignEndDate}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Campaign Description</Form.Label>
                <Form.Control
                  type="text"
                  name="campaignDescription"
                  value={formData.campaignDescription}
                  onChange={handleChange}
                  placeholder="Enter campaign description"
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="campaignIsActive"
                  label="Campaign Active"
                  checked={formData.campaignIsActive}
                  onChange={handleChange}
                />
              </Form.Group>
            </>
          )}
          
          <div className="d-flex justify-content-between mt-4">
            <Button 
              variant="secondary" 
              onClick={() => navigate('/admin/groups')}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={loading}
            >
              {loading ? 'Saving...' : (isEdit ? 'Update Group' : 'Create Group & Campaign')}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default GroupForm;
